import {
  ClampToEdgeWrapping,
  DataTexture,
  LinearFilter,
  RepeatWrapping,
  RGBAFormat,
  SRGBColorSpace,
  UnsignedByteType,
} from "three";

/**
 * Frame-LED RGB color palettes. Each palette maps the shader's `hue` (in
 * [0, 1)) onto a specific color band; the active palette index is passed
 * to the LED fragment shader as a uniform, which then samples the shared
 * `paletteLut` texture (one row per palette, sampled with linear filter).
 *
 * Layout / indexing — must match `cpbSchemaRegistry.ts`'s option-ID map:
 *   0 → "Choice 7" (default; full HSV rainbow)
 *   1 → "Choice 1" (warm + magenta)
 *   2 → "Choice 2" (cool teal / purple)
 *   3 → "Choice 3" (orange / yellow)
 *   4 → "Choice 4" (blue gradient)
 *   5 → "Choice 5" (pink / red / yellow)
 *   6 → "Choice 6" (deep red / orange)
 *
 * Keyframe closure rule: each palette's last keyframe must equal its first
 * keyframe so the wrap-around (hueLed approaches 1 → 0) doesn't show a
 * visible color seam. The full HSV palette wraps naturally via hsv(1)=hsv(0);
 * the Choice 1..6 palettes are merchant-defined one-way gradients (e.g. navy
 * → almost-white for Choice 4), which would tear at the seam if used raw.
 * `mirror()` wraps each one-way gradient into a palindrome
 * `[c0, c1, .., cN, cN-1, .., c1, c0]` so a full cycle of `hueLed` sweeps the
 * palette forward then back. The animation reads as a smooth breathing wave
 * instead of a jumpy rotating loop, and each animation mode (Classic with 3×
 * repeats, Symmetrical, Converging…) shows N palindromes around the frame
 * with no perceptible seam.
 */

export const PALETTE_COUNT = 7;
const LUT_WIDTH = 64;

type Rgb = readonly [number, number, number];

/** Adobe-style HSV→RGB, returns components in [0, 1]. */
function hsv2rgb(h: number, s: number, v: number): Rgb {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0:
      return [v, t, p];
    case 1:
      return [q, v, p];
    case 2:
      return [p, v, t];
    case 3:
      return [p, q, v];
    case 4:
      return [t, p, v];
    default:
      return [v, p, q];
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Wrap a one-way gradient `[c0, c1, .., cN]` into a palindrome
 * `[c0, c1, .., cN, cN-1, .., c1, c0]`. The result is naturally closed
 * (`last === first`) so `sampleKeyframes` produces no seam at the wrap.
 */
function mirror(colors: ReadonlyArray<Rgb>): ReadonlyArray<Rgb> {
  return [...colors, ...colors.slice(0, -1).reverse()];
}

/**
 * Sample a closed-loop keyframe gradient. `keyframes` is treated as a list of
 * segment endpoints (first == last for clean wrap). `t ∈ [0, 1)` maps onto
 * the segment chain with linear interpolation between adjacent keyframes.
 */
function sampleKeyframes(keyframes: ReadonlyArray<Rgb>, t: number): Rgb {
  const segments = keyframes.length - 1;
  const f = Math.max(0, Math.min(1 - 1e-7, t)) * segments;
  const i = Math.floor(f);
  const a = f - i;
  const c0 = keyframes[i];
  const c1 = keyframes[i + 1];
  return [lerp(c0[0], c1[0], a), lerp(c0[1], c1[1], a), lerp(c0[2], c1[2], a)];
}

/**
 * Source colors for palettes 1..6 — sampled by the merchant from each PNG
 * preview in the CPB "Choose Your RGB Color Palette" picker. Listed as
 * one-way gradients (start → end); `mirror()` wraps each into a closed
 * palindrome at LUT-build time.
 *
 * Treated as linear-light 8-bit, matching how the LED shader feeds RGB to
 * the rest of the pipeline. Output sRGB encoding happens at the framebuffer
 * stage, same as before the LUT swap.
 */
const HEX_RANGES: Record<number, ReadonlyArray<string>> = {
  // Choice 1 — pink / red / yellow → magenta / blue spectrum.
  1: ["#FC0379", "#FD3730", "#FEBB02", "#B028F1", "#1462FD", "#11A5FD"],
  // Choice 2 — cool: cyan / teal → indigo → magenta.
  2: ["#04E1C4", "#03D8E1", "#059CFA", "#4B62F2", "#7D56F4", "#DD6CFC"],
  // Choice 3 — warm sunset: red → orange → yellow.
  3: ["#FC2443", "#FD7A36", "#FEA738", "#FEBD22", "#FEE24D", "#FEEE78"],
  // Choice 4 — deep navy → sky → near-white.
  4: ["#010557", "#034EA7", "#0288CA", "#1CD5EA", "#94EBF6", "#E9FCFD"],
  // Choice 5 — pink / coral / yellow → cyan / sky.
  5: ["#FE5086", "#FE6D5E", "#FEA541", "#FCEB4A", "#3DDAE0", "#40DBE0", "#3BBFFD"],
  // Choice 6 — deep crimson → red → orange → yellow.
  6: ["#550101", "#B00304", "#ED1401", "#FD3F02", "#FD7E02", "#FDA105", "#FCE504"],
};

function hex(value: string): Rgb {
  const m = value.match(/^#?([0-9a-fA-F]{6})$/);
  if (!m) throw new Error(`palettes.ts: bad hex "${value}"`);
  const n = parseInt(m[1], 16);
  return [((n >> 16) & 0xff) / 255, ((n >> 8) & 0xff) / 255, (n & 0xff) / 255];
}

const PALETTES: Array<(t: number) => Rgb> = [
  // 0 — Choice 7 — full HSV rainbow (matches legacy hsv2rgb behavior; the
  // only palette that's natively cyclic, so no `mirror()` wrap is needed).
  (t) => hsv2rgb(t, 1, 1),
  // 1..6 — merchant-defined one-way gradients, wrapped into palindromes.
  ...[1, 2, 3, 4, 5, 6].map((id) => {
    const closed = mirror(HEX_RANGES[id].map(hex));
    return (t: number) => sampleKeyframes(closed, t);
  }),
];

let cachedLut: DataTexture | null = null;

/**
 * Build (once) the shared palette LUT — a `LUT_WIDTH × PALETTE_COUNT` RGBA
 * texture sampled by the LED fragment shader as
 * `texture2D(uPaletteLut, vec2(hue, (uPaletteIndex + 0.5) / PALETTE_COUNT))`.
 *
 * - `wrapS = RepeatWrapping` so hue wraps cleanly across the 1↔0 seam.
 * - `wrapT = ClampToEdgeWrapping` keeps each palette row in its own slot,
 *   never bleeding into neighbours.
 * - Linear filter on both axes smooths the discrete LUT samples into a
 *   continuous gradient at runtime.
 *
 * Color-space: tagged `SRGBColorSpace`. Merchant hex values (#FD3730 etc.)
 * are sRGB-encoded by definition — without this tag Three.js would treat
 * them as linear, and the secondary (non-dominant) channels would survive
 * the `× uRgbLedGain` (≈3.6) multiplier intact and wash the dominant hue
 * toward white at the framebuffer clamp (e.g. FD3730 → pink instead of red).
 * Marking the LUT sRGB applies the sRGB→linear decode at sample time, so
 * those minor channels collapse correctly under HDR gain while the primary
 * hue still punches >1 to drive bloom. Choice 7 (HSV rainbow) is unaffected
 * for primaries (0/1 values are identity under sRGB encode/decode); only
 * intermediate hues like orange now display with proper saturation.
 */
export function getPaletteLutTexture(): DataTexture {
  if (cachedLut) return cachedLut;
  const data = new Uint8Array(LUT_WIDTH * PALETTE_COUNT * 4);
  for (let row = 0; row < PALETTE_COUNT; row++) {
    const gen = PALETTES[row];
    for (let x = 0; x < LUT_WIDTH; x++) {
      const t = x / (LUT_WIDTH - 1);
      const [r, g, b] = gen(t);
      const idx = (row * LUT_WIDTH + x) * 4;
      data[idx] = Math.round(r * 255);
      data[idx + 1] = Math.round(g * 255);
      data[idx + 2] = Math.round(b * 255);
      data[idx + 3] = 255;
    }
  }
  const tex = new DataTexture(data, LUT_WIDTH, PALETTE_COUNT, RGBAFormat, UnsignedByteType);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = ClampToEdgeWrapping;
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.generateMipmaps = false;
  tex.colorSpace = SRGBColorSpace;
  tex.needsUpdate = true;
  cachedLut = tex;
  return tex;
}
